import React, { useEffect, useState } from 'react';
import { Col, Form, Input, InputNumber, Modal, Row, Select } from 'antd';
import { AddressType, CountryCode } from '../../interfaces/card-holder';
import { CitiesByStateByCountry, Countries, EXCLUDED_COUNTRIES, StatesByCountry } from '../../constants';

export type AddressesFormInputs = {
  type: AddressType;
  street: string;
  streetNumber: string;
  city: string;
  state: string;
  country: CountryCode;
  postalCode: string;
  floor: string;
  apt: string;
};

interface AddressCreateFormProps {
  open: boolean;
  onCreate: (values: AddressesFormInputs) => void;
  onCancel: () => void;
  address?: AddressesFormInputs | undefined;
}


const getStatesFromCountry = (country) =>{
  return Object.entries(StatesByCountry?.[country] ?? {}).map(([stateCode, name]) => ({
    label: name,
    value: stateCode,
  }))
}

const getCitiesFromCountryState = (country, state) => {
  return CitiesByStateByCountry?.[country]?.[state]?.toSorted()?.map((name) => ({
    label: name,
    value: name,
  }))
}

const ModalAddresses: React.FC<AddressCreateFormProps> = ({ open, onCreate, onCancel, address }) => {
  const [form] = Form.useForm();
  const country = Form.useWatch('country', form);

  const [addressStates, setAddressStates] = useState([])
  const [addressCities, setAddressCities] = useState([])

  useEffect(() => {
    if(address) {
      form.setFieldValue("type", address?.type);
      form.setFieldValue("street", address?.street);
      form.setFieldValue("streetNumber", address?.streetNumber);
      form.setFieldValue("country", address?.country);
      form.setFieldValue("state", address?.state);
      form.setFieldValue("city", address?.city);
      form.setFieldValue("postalCode", address?.postalCode);
      form.setFieldValue("floor", address?.floor);
      form.setFieldValue("apt", address?.apt);
      setAddressStates(getStatesFromCountry(address?.country))
      setAddressCities(getCitiesFromCountryState(address?.country, address?.state))
    }
  }, [address]);

  return (
    <Modal
      open={open}
      title={address ? 'Modificar direccion' : 'Crear nueva direccion'}
      okText={address ? 'Modificar' : 'Crear'}
      cancelText="Cancelar"
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      onOk={() => {
        form
          .validateFields()
          .then((values) => {
            form.resetFields();
            onCreate(values);
          })
          .catch((info) => {
            console.error('Validate Failed:', info);
          });
      }}
    >
      <Form form={form} layout="vertical" name="form_in_modal">
        <Row gutter={24}>
          <Col span={24}>
            <Form.Item<AddressesFormInputs>
              name="type"
              label="Tipo"
              rules={[{ required: true, message: 'Por favor seleccionar una opcion.' }]}
            >
              <Select
                placeholder="Seleccionar tipo"
                showSearch
                options={Object.values(AddressType).map((type) => ({ label: type, value: type }))}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={24}>
            <Form.Item<AddressesFormInputs>
              name="street"
              label="Calle"
              rules={[{ required: true, message: 'Por favor ingresar calle.' }]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={8}>
            <Form.Item<AddressesFormInputs>
              name="streetNumber"
              label="Numero"
              rules={[{ required: true, message: 'Por favor ingresar número de calle.' }]}
            >
              <InputNumber controls={false} style={{width:'100%'}}/>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item<AddressesFormInputs> name="floor" label="Piso">
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item<AddressesFormInputs> name="apt" label="Departamento">
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={24}>
            <Form.Item<AddressesFormInputs>
              name="country"
              label="Pais"
              rules={[{ required: true, message: 'Por favor ingresar pais.' }]}
            >
              <Select
                placeholder="Seleccionar pais"
                showSearch
                options={Object.entries(Countries)
                  .filter(([code]) => !EXCLUDED_COUNTRIES.includes(code))
                  .map(([code, name]) => ({ label: name, value: code }))}
                onChange={ (value) => {
                  form.setFieldValue('state', null);
                  form.setFieldValue('city', null);
                  setAddressStates(getStatesFromCountry(value))
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={8}>
            <Form.Item<AddressesFormInputs>
              name="state"
              label="Estado"
              rules={[{ required: true, message: 'Por favor ingresar estado.' }]}
            >
              <Select
                placeholder="Seleccionar estado"
                showSearch
                options={addressStates}
                onChange={ (value) => {
                  form.setFieldValue('city', null);
                  setAddressCities(getCitiesFromCountryState(country, value))
                }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item<AddressesFormInputs>
              name="city"
              label="Ciudad"
              rules={[{ required: true, message: 'Por favor ingresar ciudad.' }]}
            >
              <Select
                placeholder="Seleccionar ciudad"
                showSearch
                options={addressCities}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item<AddressesFormInputs>
              name="postalCode"
              label="Codigo Postal"
              rules={[{ required: true, message: 'Por favor ingresar codigo postal.' }]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ModalAddresses;
